import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { DowLoader } from './DowLoader';

/**
 * Protects a route by role or permission.
 *
 * Props (use one):
 *   roles      — array of allowed role strings, e.g. ['admin'] or ['admin','manager']
 *   permission — { module, action }, e.g. { module: 'leads', action: 'create' }
 *
 * On every mount it silently re-fetches the current user's permissions so that
 * changes made by an admin take effect immediately without requiring a re-login.
 * Redirects to /dashboard with a toast if the user doesn't qualify.
 */
export const PermissionRoute = ({ children, roles, permission }) => {
  const { user, loading, hasPermission } = useAuth();

  if (loading) return <DowLoader fullscreen size="xl" label="Loading" />;

  const allowed =
    roles      ? roles.includes(user?.role) :
    permission ? hasPermission(permission.module, permission.action) :
    true;

  if (!allowed) {
    return <RedirectWithToast to="/dashboard" />;
  }

  return children;
};

// Separate component so the toast fires inside a React render cycle (useEffect)
const RedirectWithToast = ({ to }) => {
  useEffect(() => {
    toast.error('You do not have permission to access that page.');
  }, []);
  return <Navigate to={to} replace />;
};
